/**
 * Full-screen media preview for chat composer.
 *
 * Opens when the user selects an image or video to attach. Lets them
 * preview at full size, edit the caption, and send — all in one
 * overlay. Replaces the cramped inline preview bar that left the
 * close X dominating the chat area.
 *
 * Audio attachments skip this — they keep the small inline preview
 * (a full-screen preview for a voice note makes no sense).
 */

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './MediaPreview.css';
import { useModalA11y } from '../../hooks/useModalA11y';

export interface MediaPreviewProps {
  fileUrl: string;
  fileType: 'image' | 'video';
  caption: string;
  sending?: boolean;
  onCaptionChange: (value: string) => void;
  onClose: () => void;
  onSend: () => void;
  /** Optional: callback for the "add another attachment" button in the
   *  composer footer. If omitted, the button is hidden. */
  onPickAnother?: () => void;
}

const MediaPreview: React.FC<MediaPreviewProps> = ({
  fileUrl,
  fileType,
  caption,
  sending = false,
  onCaptionChange,
  onClose,
  onSend,
  onPickAnother,
}) => {
  const dialogRef = useModalA11y({ onEscape: onClose });
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Auto-grow the caption textarea up to the max height defined in CSS.
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
  }, [caption]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Ctrl/Cmd+Enter sends. Plain Enter inserts a newline.
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      if (!sending) onSend();
    }
  };

  return createPortal((
    <div
      ref={dialogRef}
      className="media-preview-root"
      role="dialog"
      aria-modal="true"
      aria-label="Media preview"
      tabIndex={-1}
    >
      {/* Header */}
      <div className="media-preview-header">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close media preview"
          className="media-preview-close"
          disabled={sending}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>

        <div className="media-preview-title">Preview</div>

        {/* Spacer to balance the close button so the title stays centered. */}
        <div className="media-preview-spacer" aria-hidden="true" />
      </div>

      {/* Media stage */}
      <div className="media-preview-stage">
        {fileType === 'image' ? (
          <img
            src={fileUrl}
            alt="Selected attachment preview"
            className="media-preview-media"
            draggable={false}
          />
        ) : (
          <video
            src={fileUrl}
            controls
            playsInline
            className="media-preview-media"
          />
        )}
      </div>

      {/* Composer */}
      <div className="media-preview-composer">
        <div className="media-preview-composer-row">
          {onPickAnother && (
            <button
              type="button"
              onClick={onPickAnother}
              aria-label="Add another attachment"
              className="media-preview-icon-btn"
              disabled={sending}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
            </button>
          )}

          <div className="media-preview-caption-wrap">
            <textarea
              ref={textareaRef}
              value={caption}
              onChange={e => onCaptionChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Add a caption…"
              rows={1}
              className="media-preview-caption"
              disabled={sending}
            />
          </div>

          <button
            type="button"
            onClick={onSend}
            aria-label="Send media"
            className="media-preview-send"
            disabled={sending}
          >
            {sending ? (
              <span className="media-preview-spinner" aria-hidden="true" />
            ) : (
              <svg viewBox="0 0 24 24" fill="currentColor" width="19" height="19">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
};

export default MediaPreview;
